import { createNewLabelHierarchy } from "../labels_list/utils.jsx";

// A hierarchy subAccounts entry may be a bare id string OR a legacy `{id}`
// object. Every read boundary must normalize to the string id — the object
// form otherwise fails identity/string operations (invisible rows, broken
// React keys, un-prunable entries). Shared here so all read sites agree.
export function normalizeSubAccountId(entry) {
  return typeof entry === "string" ? entry : entry?.id;
}

export const findAccountType = (accountNumber) => {
  //Get the first digit of the account number
  const firstDigit = parseInt(accountNumber.toString().slice(0, 1));
  if (firstDigit === 1) {
    return "Assets";
  } else if (firstDigit === 2) {
    return "Liabilities";
  } else if (firstDigit === 3) {
    return "Equity";
  } else if (firstDigit === 4) {
    return "Income";
  } else if (firstDigit >= 5) {
    return "Expenses";
  } else {
    return "Invalid account number";
  }
};

export const createNewHierarchy = (
  accountsHierarchy,
  type,
  groupName,
  accountId,
  parentId,
  subAccountIds,
) => {
  const typeOrder = ["Assets", "Liabilities", "Income", "Expenses", "Equity"];

  console.log(
    "createNewHierarchy FROM:",
    accountsHierarchy,
    type,
    groupName,
    accountId,
    parentId,
  );
  console.log("ParendID: ", parentId);
  if (!type || !groupName?.trim() || !accountId?.trim()) {
    console.error("Invalid: ", type, groupName, accountId);
    return null;
  } // Handling invalid inputs

  // If accountsHierarchy is null or undefined, create an empty hierarchy
  if (!accountsHierarchy) {
    return {
      types: [
        {
          type: type,
          groups: [
            {
              groupName: groupName,
              accounts: [{ id: accountId, subAccounts: [] }],
            },
          ],
        },
      ],
    };
  }

  // Check if the type exists
  const existingType = accountsHierarchy.types?.find((t) => t.type === type);

  if (!existingType) {
    const newTypes =
      accountsHierarchy?.types?.length > 0
        ? [
            ...accountsHierarchy.types,
            { type, groups: [{ groupName, accounts: [{ id: accountId }] }] },
          ]
        : [{ type, groups: [{ groupName, accounts: [{ id: accountId }] }] }];
    newTypes.sort((a, b) => {
      const orderA = typeOrder.indexOf(a.type);
      const orderB = typeOrder.indexOf(b.type);
      return orderA - orderB;
    });
    return { ...accountsHierarchy, types: newTypes };
  }

  // Check if the group exists within the type
  const existingGroup = existingType.groups?.find(
    (g) => g.groupName === groupName,
  );

  if (!existingGroup) {
    const updatedTypes = accountsHierarchy.types.map((t) =>
      t.type === type
        ? {
            ...t,
            groups: [
              ...t.groups,
              {
                groupName,
                accounts: [
                  {
                    id: accountId,
                    subAccounts: subAccountIds?.length > 0 ? subAccountIds : [],
                  },
                ],
              },
            ],
          }
        : t,
    );
    updatedTypes.sort((a, b) => {
      const orderA = typeOrder.indexOf(a.type);
      const orderB = typeOrder.indexOf(b.type);
      return orderA - orderB;
    });
    return { ...accountsHierarchy, types: updatedTypes };
  }

  // If the group exists, add the account to it
  console.log("Existing group found: ", existingGroup);
  const updatedHierarchyTypes = accountsHierarchy.types.map((t) =>
    t.type === type
      ? {
          ...t,
          groups: t.groups.map((g) => {
            if (g.groupName === groupName) {
              let updatedAccounts;

              // If there's a parentId, append to that parent's subAccounts —
              // rebuilding the parent object (never mutating the shared one).
              if (parentId) {
                updatedAccounts = g.accounts.some((acc) => acc.id === parentId)
                  ? g.accounts.map((acc) =>
                      acc.id === parentId
                        ? {
                            ...acc,
                            subAccounts: [
                              ...(acc.subAccounts || []),
                              accountId,
                            ],
                          }
                        : acc,
                    )
                  : [...g.accounts];
              } else {
                // No parentId, just add the account normally
                updatedAccounts = [
                  ...g.accounts,
                  {
                    id: accountId,
                    subAccounts: subAccountIds?.length > 0 ? subAccountIds : [],
                  },
                ];
              }

              return { ...g, accounts: updatedAccounts };
            }
            return g;
          }),
        }
      : t,
  );
  updatedHierarchyTypes.sort((a, b) => {
    const orderA = typeOrder.indexOf(a.type);
    const orderB = typeOrder.indexOf(b.type);
    return orderA - orderB;
  });
  return { ...accountsHierarchy, types: updatedHierarchyTypes };
};

export const createHierarchyFromMassImport = (groupsMapping, accountIds) => {
  console.log("createHierarchyFromMassImport is being called");
  console.log("createHierarchyFromMassImport", groupsMapping, accountIds);
  const typesPossible = [
    "Assets",
    "Liabilities",
    "Equity",
    "Income",
    "Expenses",
  ];
  const tempArr = [];
  typesPossible.forEach((type) => {
    console.log("Processing type: ", type);
    const accountsByType = Object.entries(groupsMapping).filter(
      ([accountNumber, accountData]) => accountData.type === type,
    );
    console.log("accountsByType for type " + type + ": ", accountsByType);
    if (accountsByType.length > 0) {
      const groupsByType = accountsByType.map(
        ([accountNumber, accountData]) => {
          return accountData.group;
        },
      );
      const uniqueGroups = [...new Set(groupsByType)];
      const groupsArr = [];
      console.log("uniqueGroups: ", uniqueGroups);
      uniqueGroups.forEach((group) => {
        console.log("group: ", group);
        const accountsByGroup = accountsByType.filter(
          ([accountNumber, accountData]) => accountData.group === group,
        );
        console.log("accountsByGroup: ", accountsByGroup);
        groupsArr.push({
          groupName: group,
          accounts: accountsByGroup.map(([accountNumber, accountData]) => {
            console.log("accountNumber: ", accountNumber);
            const accountObj = accountIds.find((account) => {
              console.log("account: ", account, accountNumber);
              return account[accountNumber] !== undefined;
            });
            return accountObj ? { id: accountObj[accountNumber] } : undefined;
          }),
        });
      });
      tempArr.push({
        type: type,
        groups: groupsArr,
      });
    }
  });

  console.log("sortedTempArr: ", tempArr);
  return { types: tempArr };
};

export const removeHierarchyAccount = (accountsHierarchy, accountId) => {
  if (!accountsHierarchy) {
    return null;
  }

  const removeAccountFromArr = (accounts) => {
    return accounts
      .map((account) => {
        // Create a new account object with updated subAccounts
        if (account.subAccounts) {
          return {
            ...account,
            // Normalize each entry so a legacy `{id}` object is matched and
            // removed too (a raw `entry !== accountId` never matches an object).
            subAccounts: account.subAccounts.filter(
              (entry) => normalizeSubAccountId(entry) !== accountId,
            ),
          };
        }
        return { ...account }; // Create a copy even if no subAccounts
      })
      .filter((account) => account.id !== accountId);
  };

  const updatedTypes = accountsHierarchy.types.map((t) => {
    const updatedGroups = t.groups.map((g) => {
      const updatedAccounts = removeAccountFromArr(g.accounts);
      return { ...g, accounts: updatedAccounts };
    });

    const nonEmptyGroups = updatedGroups.filter((g) => g.accounts.length > 0);
    return { ...t, groups: nonEmptyGroups };
  });

  return { ...accountsHierarchy, types: updatedTypes };
};

export const findAccountsGroupInHierarchy = (accountsHierarchy, accountId) => {
  if (!accountsHierarchy) {
    return null;
  }

  const group = accountsHierarchy.types
    .map((t) => t.groups)
    .flat()
    .find((g) => {
      return g.accounts.find((a) => a.id === accountId);
    });

  return group;
};

//Search the hierarchy to find if an account is in a subAccounts array
export const findIfSubAccountInHierarchy = (accountsHierarchy, accountId) => {
  if (!accountsHierarchy) {
    return null;
  }

  const groupsWithThisAsSubaccount = accountsHierarchy.types
    .map((t) => t.groups)
    .flat()
    .map((g) => g.accounts)
    .flat()
    .filter((group) => {
      return group.subAccounts?.includes(accountId);
    });

  const isASubAccount = groupsWithThisAsSubaccount.length > 0;

  console.log("SubAccount? : ", isASubAccount);

  return isASubAccount;
};

export const createNewFundHierarchy = (
  fundsHierarchy,
  groupName,
  fundId,
  parentId,
  subFundIds,
) => {
  console.log(
    "createNewFundHierarchy FROM:",
    fundsHierarchy,
    groupName,
    fundId,
    parentId,
    subFundIds,
  );

  if (!groupName?.trim() || !fundId?.trim()) {
    console.error("Invalid: ", groupName, fundId);
    return null;
  } // Handling invalid inputs

  // If fundsHierarchy is null or undefined, create an empty hierarchy
  if (!fundsHierarchy) {
    return {
      groups: [
        {
          groupName: groupName,
          funds: [{ id: fundId }],
        },
      ],
    };
  }

  // Check if the group exists
  const existingGroup = fundsHierarchy.groups?.find(
    (g) => g.groupName === groupName,
  );

  if (!existingGroup) {
    const newGroups =
      fundsHierarchy?.groups?.length > 0
        ? [
            ...fundsHierarchy.groups,
            {
              groupName,
              funds: [
                {
                  id: fundId,
                  subFunds: subFundIds?.length > 0 ? subFundIds : [],
                },
              ],
            },
          ]
        : [{ groupName, funds: [{ id: fundId }] }];
    return { ...fundsHierarchy, groups: newGroups };
  }

  // If the group exists, add the fund to it
  console.log("Existing group found: ", existingGroup);
  const updatedHierarchyGroups = fundsHierarchy.groups.map((g) => {
    if (g.groupName === groupName) {
      // Append to the parent's subFunds — rebuilding the parent object rather
      // than mutating the shared one.
      if (parentId && g.funds.some((fund) => fund.id === parentId)) {
        return {
          ...g,
          funds: g.funds.map((fund) =>
            fund.id === parentId
              ? { ...fund, subFunds: [...(fund.subFunds || []), fundId] }
              : fund,
          ),
        };
      }
      // No parentId, or parentFund not found. Add the fund normally.
      return {
        ...g,
        funds: [
          ...g.funds,
          { id: fundId, subFunds: subFundIds?.length > 0 ? subFundIds : [] },
        ],
      };
    }
    return g;
  });
  return { ...fundsHierarchy, groups: updatedHierarchyGroups };
};

export const removeFundHierarchy = (fundsHierarchy, fundId) => {
  if (!fundsHierarchy) {
    return null;
  }

  const removeFundFromArr = (funds) => {
    return funds
      .map((fund) => {
        // Rebuild the fund with subFunds pruned (never mutate the shared object)
        if (fund.subFunds) {
          return {
            ...fund,
            subFunds: fund.subFunds.filter((id) => id !== fundId),
          };
        }
        return { ...fund };
      })
      .filter((fund) => fund.id !== fundId);
  };

  const updatedGroups = fundsHierarchy.groups.map((g) => {
    const updatedFunds = removeFundFromArr(g.funds);
    return { ...g, funds: updatedFunds };
  });

  const nonEmptyGroups = updatedGroups.filter((g) => g.funds.length > 0);

  return { ...fundsHierarchy, groups: nonEmptyGroups };
};

export const findIfSubFundInHierarchy = (fundsHierarchy, fundId) => {
  if (!fundsHierarchy) {
    return null;
  }

  // Flatten all groups' funds into a single array
  const allFunds = (fundsHierarchy.groups || [])
    .map((group) => group.funds)
    .flat();

  // Check if the fund appears in any subFunds array
  const fundsWithThisAsSubfund = allFunds.filter((fund) => {
    return fund.subFunds?.includes(fundId);
  });

  const isASubFund = fundsWithThisAsSubfund.length > 0;

  console.log("Currently a Subfund? : ", isASubFund);

  return isASubFund;
};

export const findFundsGroupInHierarchy = (fundsHierarchy, fundId) => {
  if (!fundsHierarchy) {
    return null;
  }

  const group = (fundsHierarchy.groups || []).find((g) => {
    return g.funds.find((f) => f.id === fundId);
  });

  return group;
};

/**
 * Ensures all accounts from the accounts collection are represented in the accountsHierarchy.
 * Any missing accounts will be added to the first group of their appropriate account type.
 * This function fixes the issue where accounts exist in the database but are missing from the hierarchy.
 *
 * @param {Object} accountsHierarchy - The current accounts hierarchy
 * @param {Array} accounts - Array of all accounts from the database
 * @returns {Object} Updated hierarchy with all accounts included
 */
export const ensureAllAccountsInHierarchy = (accountsHierarchy, accounts) => {
  console.log(
    "🔍 Starting hierarchy validation - ensuring all accounts are included",
  );

  if (!accounts || accounts.length === 0) {
    console.log("📋 No accounts to validate");
    return accountsHierarchy;
  }

  if (!accountsHierarchy || !accountsHierarchy.types) {
    console.log("⚠️ No hierarchy exists, creating from scratch");
    // If no hierarchy exists, we'll need to create one from all accounts
    const defaultGroupNames = {
      Assets: "Assets",
      Liabilities: "Liabilities",
      Income: "Income",
      Expenses: "Expenses",
      Equity: "Equity",
    };

    let newHierarchy = { types: [] };

    accounts.forEach((account) => {
      const accountType = account.accountType;

      // Skip Equity accounts - they're managed through the funds system
      if (accountType === "Equity") {
        return;
      }

      const groupName = defaultGroupNames[accountType] || accountType;

      newHierarchy = createNewHierarchy(
        newHierarchy,
        accountType,
        groupName,
        account.id,
        null,
        null,
      );
    });

    return newHierarchy;
  }

  // Get all account IDs currently in the hierarchy
  const hierarchyAccountIds = new Set();
  const hierarchyAccountDetails = []; // For debugging

  accountsHierarchy.types.forEach((type) => {
    type.groups.forEach((group) => {
      group.accounts.forEach((account) => {
        hierarchyAccountIds.add(account.id);
        hierarchyAccountDetails.push({
          id: account.id,
          type: type.type,
          group: group.groupName,
          isSubAccount: false,
        });
        // Also add sub-accounts
        if (account.subAccounts) {
          account.subAccounts.forEach((subAccountId) => {
            hierarchyAccountIds.add(subAccountId);
            hierarchyAccountDetails.push({
              id: subAccountId,
              type: type.type,
              group: group.groupName,
              isSubAccount: true,
              parentId: account.id,
            });
          });
        }
      });
    });
  });

  console.log(
    `📊 Found ${hierarchyAccountIds.size} accounts in hierarchy, ${accounts.length} accounts in database`,
  );

  // Debug: Show what's actually in the hierarchy
  console.log("🏗️ Current hierarchy structure:", hierarchyAccountDetails);

  // Debug: Show database account types breakdown
  const accountTypeBreakdown = accounts.reduce((acc, account) => {
    const type = account.accountType || "NULL/UNDEFINED";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});
  console.log("📋 Database account types breakdown:", accountTypeBreakdown);

  // Debug: Show some examples of each account type
  Object.keys(accountTypeBreakdown).forEach((type) => {
    const exampleAccounts = accounts
      .filter((acc) => (acc.accountType || "NULL/UNDEFINED") === type)
      .slice(0, 3) // Just first 3 examples
      .map((acc) => ({
        id: acc.id,
        name: acc.accountName,
        number: acc.accountNumber,
        type: acc.accountType,
      }));
    console.log(`📝 Example ${type} accounts:`, exampleAccounts);
  });

  // Find accounts that are missing from hierarchy (excluding Equity accounts)
  const missingAccounts = accounts.filter(
    (account) =>
      !hierarchyAccountIds.has(account.id) && account.accountType !== "Equity",
  );

  if (missingAccounts.length === 0) {
    console.log("✅ All accounts are properly represented in hierarchy");
    return accountsHierarchy;
  }

  console.log(
    `🚨 Found ${missingAccounts.length} accounts missing from hierarchy:`,
    missingAccounts.map((acc) => ({
      id: acc.id,
      name: acc.accountName,
      number: acc.accountNumber,
      type: acc.accountType,
      displayName: `${acc.accountName} (#${acc.accountNumber})`,
    })),
  );

  // Add missing accounts to the hierarchy
  let updatedHierarchy = { ...accountsHierarchy };

  missingAccounts.forEach((account) => {
    const accountType = account.accountType;
    const displayName = `${account.accountName} (#${account.accountNumber})`;

    // Validate account type - skip if invalid or Equity
    const validAccountTypes = ["Assets", "Liabilities", "Income", "Expenses"];
    if (!accountType || !validAccountTypes.includes(accountType)) {
      console.log(
        `⚠️ Skipping account ${displayName} - invalid account type: ${accountType}`,
      );
      return;
    }

    // Find the first group for this account type, or create a default one
    const typeObj = updatedHierarchy.types.find((t) => t.type === accountType);
    let groupName;

    if (typeObj && typeObj.groups.length > 0) {
      // Use the first existing group for this type
      groupName = typeObj.groups[0].groupName;
      console.log(
        `📌 Adding account ${displayName} to existing group "${groupName}" in ${accountType}`,
      );
    } else {
      // Create a default group name for this type
      groupName = accountType;
      console.log(
        `📌 Adding account ${displayName} to new default group "${groupName}" in ${accountType}`,
      );
    }

    // Add the account to the hierarchy
    updatedHierarchy = createNewHierarchy(
      updatedHierarchy,
      accountType,
      groupName,
      account.id,
      null, // Not a sub-account
      null, // No sub-accounts
    );
  });

  console.log(
    `✅ Successfully added ${missingAccounts.length} missing accounts to hierarchy`,
  );
  return updatedHierarchy;
};

/**
 * Ensures all funds from the funds collection are represented in the fundsHierarchy.
 * Any missing valid funds will be added to the first existing group.
 * Skips blank/invalid funds (empty name or number) to avoid adding garbage to hierarchy.
 *
 * @param {Object} fundsHierarchy - The current funds hierarchy
 * @param {Array} funds - Array of all funds from the database
 * @returns {Object} Updated hierarchy with all valid funds included
 */
export const ensureAllFundsInHierarchy = (fundsHierarchy, funds) => {
  console.log(
    "🔍 Starting fund hierarchy validation - ensuring all funds are included",
  );

  if (!funds || funds.length === 0) {
    console.log("📋 No funds to validate");
    return fundsHierarchy;
  }

  if (!fundsHierarchy || !fundsHierarchy.groups) {
    console.log("⚠️ No fund hierarchy exists, creating from scratch");
    let newHierarchy = { groups: [] };
    funds.forEach((fund) => {
      // Skip blank/invalid funds
      if (!fund.fundName?.trim() || !fund.fundNumber?.toString().trim()) {
        return;
      }
      newHierarchy = createNewFundHierarchy(
        newHierarchy,
        "Funds",
        fund.id,
        null,
        null,
      );
    });
    return newHierarchy;
  }

  // Get all fund IDs currently in the hierarchy (top-level + subFunds)
  const hierarchyFundIds = new Set();
  fundsHierarchy.groups.forEach((group) => {
    group.funds.forEach((fund) => {
      hierarchyFundIds.add(fund.id);
      if (fund.subFunds) {
        fund.subFunds.forEach((subFundId) => {
          hierarchyFundIds.add(subFundId);
        });
      }
    });
  });

  console.log(
    `📊 Found ${hierarchyFundIds.size} funds in hierarchy, ${funds.length} funds in database`,
  );

  // Find funds that are missing from hierarchy, skipping blank/invalid ones
  const missingFunds = funds.filter((fund) => {
    if (hierarchyFundIds.has(fund.id)) return false;
    // Skip blank/invalid funds
    if (!fund.fundName?.trim() || !fund.fundNumber?.toString().trim()) {
      console.log(
        `⚠️ Skipping blank/invalid fund ${fund.id} - name: "${fund.fundName}", number: "${fund.fundNumber}"`,
      );
      return false;
    }
    return true;
  });

  if (missingFunds.length === 0) {
    console.log("✅ All valid funds are properly represented in hierarchy");
    return fundsHierarchy;
  }

  console.log(
    `🚨 Found ${missingFunds.length} funds missing from hierarchy:`,
    missingFunds.map((f) => ({
      id: f.id,
      name: f.fundName,
      number: f.fundNumber,
    })),
  );

  // Add missing funds to the first existing group
  let updatedHierarchy = { ...fundsHierarchy };
  missingFunds.forEach((fund) => {
    const groupName =
      updatedHierarchy.groups.length > 0
        ? updatedHierarchy.groups[0].groupName
        : "Funds";

    console.log(
      `📌 Adding fund "${fund.fundName}" (#${fund.fundNumber}) to group "${groupName}"`,
    );

    updatedHierarchy = createNewFundHierarchy(
      updatedHierarchy,
      groupName,
      fund.id,
      null,
      null,
    );
  });

  console.log(
    `✅ Successfully added ${missingFunds.length} missing funds to hierarchy`,
  );
  return updatedHierarchy;
};

/**
 * Ensures all labels from the labels collection are represented in the labelsHierarchy.
 * Any missing valid labels will be added to the first existing group.
 * Skips blank/invalid labels (empty name) to avoid adding garbage to hierarchy.
 *
 * @param {Object} labelsHierarchy - The current labels hierarchy
 * @param {Array} labels - Array of all labels from the database
 * @returns {Object} Updated hierarchy with all valid labels included
 */
export const ensureAllLabelsInHierarchy = (labelsHierarchy, labels) => {
  console.log(
    "🔍 Starting label hierarchy validation - ensuring all labels are included",
  );

  if (!labels || labels.length === 0) {
    console.log("📋 No labels to validate");
    return labelsHierarchy;
  }

  if (!labelsHierarchy || !labelsHierarchy.groups) {
    console.log("⚠️ No label hierarchy exists, creating from scratch");
    let newHierarchy = { groups: [] };
    labels.forEach((label) => {
      // Skip blank/invalid labels
      if (!label.labelName?.trim()) {
        return;
      }
      newHierarchy = createNewLabelHierarchy(
        newHierarchy,
        "Labels",
        label.id,
        null,
        null,
      );
    });
    return newHierarchy;
  }

  // Get all label IDs currently in the hierarchy (top-level + subLabels)
  const hierarchyLabelIds = new Set();
  labelsHierarchy.groups.forEach((group) => {
    group.labels.forEach((label) => {
      hierarchyLabelIds.add(label.id);
      if (label.subLabels) {
        label.subLabels.forEach((subLabelId) => {
          hierarchyLabelIds.add(subLabelId);
        });
      }
    });
  });

  console.log(
    `📊 Found ${hierarchyLabelIds.size} labels in hierarchy, ${labels.length} labels in database`,
  );

  // Find labels that are missing from hierarchy, skipping blank/invalid ones
  const missingLabels = labels.filter((label) => {
    if (hierarchyLabelIds.has(label.id)) return false;
    // Skip blank/invalid labels
    if (!label.labelName?.trim()) {
      console.log(
        `⚠️ Skipping blank/invalid label ${label.id} - name: "${label.labelName}"`,
      );
      return false;
    }
    return true;
  });

  if (missingLabels.length === 0) {
    console.log("✅ All valid labels are properly represented in hierarchy");
    return labelsHierarchy;
  }

  console.log(
    `🚨 Found ${missingLabels.length} labels missing from hierarchy:`,
    missingLabels.map((l) => ({
      id: l.id,
      name: l.labelName,
      number: l.labelNumber,
    })),
  );

  // Add missing labels to the first existing group
  let updatedHierarchy = { ...labelsHierarchy };
  missingLabels.forEach((label) => {
    const groupName =
      updatedHierarchy.groups.length > 0
        ? updatedHierarchy.groups[0].groupName
        : "Labels";

    console.log(
      `📌 Adding label "${label.labelName}" (#${label.labelNumber || "none"}) to group "${groupName}"`,
    );

    updatedHierarchy = createNewLabelHierarchy(
      updatedHierarchy,
      groupName,
      label.id,
      null,
      null,
    );
  });

  console.log(
    `✅ Successfully added ${missingLabels.length} missing labels to hierarchy`,
  );
  return updatedHierarchy;
};

/**
 * One-time utility function to fix existing hierarchy issues.
 * This can be called manually to repair a broken hierarchy where accounts are missing.
 *
 * @param {Object} db - Firestore database instance (v9 modular SDK)
 * @param {string} org - Organization ID
 * @returns {Promise} Promise that resolves when the hierarchy is fixed
 */
export const fixExistingAccountHierarchy = async (db, org) => {
  // Import Firebase v9 functions
  const { collection, doc, getDocs, getDoc, setDoc } = await import(
    "firebase/firestore"
  );

  console.log("🔧 Starting one-time hierarchy repair for organization:", org);

  try {
    // Get current accounts from database using v9 syntax
    const accountsCollection = collection(db, "orgs", org, "accounts");
    const accountsSnapshot = await getDocs(accountsCollection);
    const accounts = accountsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log(`📊 Found ${accounts.length} accounts in database`);

    // Get current hierarchy using v9 syntax
    const hierarchyRef = doc(
      db,
      "orgs",
      org,
      "organizationAndSettings",
      "accountsHierarchy",
    );
    const hierarchyDoc = await getDoc(hierarchyRef);
    let currentHierarchy = null;

    if (hierarchyDoc.exists()) {
      currentHierarchy = hierarchyDoc.data();
      console.log("📋 Current hierarchy exists");
    } else {
      console.log("⚠️ No hierarchy document found");
    }

    // Fix the hierarchy
    const fixedHierarchy = ensureAllAccountsInHierarchy(
      currentHierarchy,
      accounts,
    );

    // Save the fixed hierarchy using v9 syntax
    await setDoc(hierarchyRef, fixedHierarchy, { merge: true });

    console.log("✅ Hierarchy repair completed successfully!");

    // Get details about what was fixed for reporting
    const hierarchyAccountIds = new Set();
    const hierarchyAccountDetails = []; // For debugging
    if (currentHierarchy?.types) {
      currentHierarchy.types.forEach((type) => {
        type.groups.forEach((group) => {
          group.accounts.forEach((account) => {
            hierarchyAccountIds.add(account.id);
            hierarchyAccountDetails.push({
              id: account.id,
              type: type.type,
              group: group.groupName,
              isSubAccount: false,
            });
            if (account.subAccounts) {
              account.subAccounts.forEach((subAccountId) => {
                hierarchyAccountIds.add(subAccountId);
                hierarchyAccountDetails.push({
                  id: subAccountId,
                  type: type.type,
                  group: group.groupName,
                  isSubAccount: true,
                  parentId: account.id,
                });
              });
            }
          });
        });
      });
    }

    const missingAccounts = accounts.filter(
      (account) =>
        !hierarchyAccountIds.has(account.id) &&
        account.accountType !== "Equity",
    );

    // Filter missing accounts to only include those that would actually be processed
    // (excluding Equity accounts and invalid account types)
    const validAccountTypes = ["Assets", "Liabilities", "Income", "Expenses"];

    // Debug: Log all missing accounts and their types
    console.log(
      "🔍 All missing accounts found:",
      missingAccounts.map((acc) => ({
        id: acc.id,
        name: acc.accountName,
        number: acc.accountNumber,
        type: acc.accountType,
        displayName: `${acc.accountName} (#${acc.accountNumber})`,
      })),
    );

    // Debug: For each missing account, check if it might be in hierarchy under different circumstances
    missingAccounts.forEach((acc) => {
      const hierarchyEntry = hierarchyAccountDetails.find(
        (h) => h.id === acc.id,
      );
      if (hierarchyEntry) {
        console.log(
          `🤔 WEIRD: Account ${acc.accountName} (#${acc.accountNumber}) is flagged as missing but found in hierarchy:`,
          hierarchyEntry,
        );
      } else {
        console.log(
          `❓ Account ${acc.accountName} (#${acc.accountNumber}) with type "${acc.accountType}" is genuinely not in hierarchy`,
        );
      }
    });

    const actuallyProcessedAccounts = missingAccounts.filter(
      (account) =>
        account.accountType && validAccountTypes.includes(account.accountType),
    );

    // Debug: Log accounts that will actually be processed
    console.log(
      "✅ Accounts that will actually be processed:",
      actuallyProcessedAccounts.map((acc) => ({
        name: acc.accountName,
        number: acc.accountNumber,
        type: acc.accountType,
        displayName: `${acc.accountName} (#${acc.accountNumber})`,
      })),
    );

    // Create detailed report of what was actually fixed
    const fixedAccountDetails = actuallyProcessedAccounts.map((account) => {
      const typeObj = fixedHierarchy.types.find(
        (t) => t.type === account.accountType,
      );
      const groupName = typeObj?.groups?.[0]?.groupName || account.accountType;

      return {
        name: account.accountName,
        number: account.accountNumber,
        type: account.accountType,
        insertedInGroup: groupName,
        displayName: `${account.accountName} (#${account.accountNumber})`,
      };
    });

    return {
      success: true,
      accountsProcessed: accounts.length,
      hierarchyFixed: true,
      discrepanciesFound: actuallyProcessedAccounts.length,
      fixedAccounts: fixedAccountDetails,
    };
  } catch (error) {
    console.error("❌ Error during hierarchy repair:", error);
    throw error;
  }
};

/**
 * Cleanup function to remove accounts with invalid/unknown account types from hierarchy.
 * This fixes issues where accounts with null/undefined/invalid account types got added.
 *
 * @param {Object} db - Firestore database instance (v9 modular SDK)
 * @param {string} org - Organization ID
 * @returns {Promise} Promise that resolves when cleanup is complete
 */
export const cleanupUnknownAccountsFromHierarchy = async (db, org) => {
  // Import Firebase v9 functions
  const { doc, getDoc, setDoc } = await import("firebase/firestore");

  console.log(
    "🧹 Starting cleanup of unknown/invalid accounts from hierarchy...",
  );

  try {
    // Get current hierarchy
    const hierarchyRef = doc(
      db,
      "orgs",
      org,
      "organizationAndSettings",
      "accountsHierarchy",
    );
    const hierarchyDoc = await getDoc(hierarchyRef);

    if (!hierarchyDoc.exists()) {
      console.log("⚠️ No hierarchy document found");
      return { success: false, message: "No hierarchy found" };
    }

    const currentHierarchy = hierarchyDoc.data();
    let cleanedHierarchy = { ...currentHierarchy };
    let removedCount = 0;
    const removedAccounts = [];

    // Valid account types
    const validAccountTypes = [
      "Assets",
      "Liabilities",
      "Income",
      "Expenses",
      "Equity",
    ];

    // Clean up invalid account types and empty groups
    cleanedHierarchy.types = cleanedHierarchy.types
      .filter((type) => {
        if (!validAccountTypes.includes(type.type)) {
          console.log(`🗑️ Removing invalid account type: ${type.type}`);
          // Count accounts being removed
          type.groups?.forEach((group) => {
            group.accounts?.forEach((account) => {
              removedCount++;
              removedAccounts.push({
                id: account.id,
                type: type.type,
                group: group.groupName,
              });
            });
          });
          return false;
        }
        return true;
      })
      .map((type) => {
        // Clean up empty groups within valid types
        type.groups = type.groups.filter((group) => {
          return group.accounts && group.accounts.length > 0;
        });
        return type;
      })
      .filter((type) => {
        // Remove types that have no groups left
        return type.groups && type.groups.length > 0;
      });

    // Save the cleaned hierarchy
    await setDoc(hierarchyRef, cleanedHierarchy, { merge: true });

    console.log(
      `🧹 Cleanup completed! Removed ${removedCount} accounts from invalid types.`,
    );

    return {
      success: true,
      accountsRemoved: removedCount,
      removedAccounts: removedAccounts,
      message: `Removed ${removedCount} accounts from invalid account types`,
    };
  } catch (error) {
    console.error("❌ Error during hierarchy cleanup:", error);
    throw error;
  }
};
